import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';

import FirstAidKitFill from '@/assets/icons/first_aid_kit_fill.svg?component';
import FishFill from '@/assets/icons/fish_fill.svg?component';
import { Spinner } from '@/components/atoms/Spinner';
import Tooltip from '@/components/atoms/Tooltip';
import { renderTrendIcon } from '@/components/atoms/TrendIcon/TrendIconUtils';
import PageLayout from '@/components/Layout/PageLayout';
import MetricCard from '@/components/molecules/MetricCard/MetricCard';
import type { Image } from '@/components/organisms/ImageViewer';
import SomethingHappened from '@/components/SomethingHappened';
import LICENSES, { type LicenseCode } from '@/config/licenses';
import { useCage, useCageName } from '@/contexts/cage';
import type { WelfareOverview } from '@/domain/client';
import type { WelfareData } from '@/domain/welfare/types';
import { useCageOverview } from '@/routes/Client/Cage/components/cage-overview-context';
import { createCageUrl } from '@/utils/urlGenerator';

import DetectionsCard from './components/DetectionsCard';
import WoundInsightCard from './components/WoundInsightCard';
import { useWelfareSummary } from './useWelfareSummary';

import styles from './CageWelfareDashboard.module.scss';

interface CageWelfareDashboardPageContentProps {
    clientId: number;
    locationId: number;
    cageId: number;
    images?: Image[];
    cageWelfareOverview: WelfareOverview | undefined;
    cageSpeed: number | undefined;
    detectionsLoading: boolean;
    fishHealthData?: WelfareData;
}

export default function CageWelfareDashboardPageContent(
    props: CageWelfareDashboardPageContentProps
) {
    const {
        clientId,
        locationId,
        cageId,
        images,
        cageWelfareOverview,
        cageSpeed,
        detectionsLoading,
        fishHealthData,
    } = props;
    const { loading, error, isFetching, refetch } = useCageOverview();
    const { hasLicenseByCode } = useCage();
    const cageName = useCageName();

    const {
        woundsInfoText,
        woundsInfoTooltip,
        woundsInfoTrend,
        swimSpeedInfoText,
        lastFishHealthDay,
        lastEstimate,
    } = useWelfareSummary({ cageWelfareOverview, cageSpeed, fishHealthData });

    const isBehaviorModuleEnabled = hasLicenseByCode(LICENSES.BEHAVIOR as LicenseCode);

    const navigate = useNavigate();

    if (loading) {
        return <Spinner size="lg" fullScreen />;
    }

    // The page's title goes with the content, so the error state stands in as its h1.
    if (error) {
        return (
            <SomethingHappened
                error={error}
                headingLevel="h1"
                onRetry={refetch}
                isRetrying={isFetching}
            />
        );
    }

    return (
        <PageLayout title={cageName} subtitle={t('common.labels.welfare')}>
            <div className={styles.weightMainItemsContainer}>
                <Tooltip content={woundsInfoTooltip}>
                    <MetricCard
                        title={t('cageWelfare.common.totalNumberOfWounds')}
                        icon={FirstAidKitFill}
                        tone="success"
                        value={woundsInfoText}
                        framed="surface"
                        trend={renderTrendIcon(woundsInfoTrend)}
                    />
                </Tooltip>

                {!isBehaviorModuleEnabled && (
                    <MetricCard
                        title={t('common.labels.swimSpeed')}
                        icon={FishFill}
                        tone="neutral"
                        value={swimSpeedInfoText}
                        framed="surface"
                    />
                )}
            </div>

            <div className={styles.pageBoxLayout}>
                <div className={styles.leftSideContainer}>
                    <WoundInsightCard
                        lastFishHealthDay={lastFishHealthDay}
                        lastEstimate={lastEstimate}
                        onView={() =>
                            navigate(
                                `${createCageUrl(clientId, locationId, cageId)}/welfare/wounds`
                            )
                        }
                    />
                </div>
                <DetectionsCard
                    images={images}
                    detectionsLoading={detectionsLoading}
                    onView={() =>
                        navigate(
                            `${createCageUrl(clientId, locationId, cageId)}/welfare/wounds/detections`
                        )
                    }
                />
            </div>
        </PageLayout>
    );
}
